import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  LogOut, Calendar, Clock, MapPin, CreditCard, 
  CheckCircle, XCircle, AlertCircle, ChevronRight, User, Search, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchBookings = async () => {
      try {
        const response = await axios.get(`${API}/bookings`, { withCredentials: true });
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [isAuthenticated, navigate]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      completed: 'bg-slate-100 text-slate-700 border-slate-200',
      cancelled: 'bg-rose-50 text-rose-700 border-rose-200'
    };
    return styles[status] || styles.pending;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      unpaid: 'bg-rose-50 text-rose-700 border-rose-200',
      paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      refunded: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return styles[status] || styles.unpaid;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-dashboard">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="font-display text-xl font-bold text-slate-900 tracking-tight">
              My Detective
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
                <Search className="w-4 h-4" />
                Find Agencies
              </Link>
              <Link to="/chats" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                Messages
              </Link>
              
              {/* User Profile Section */}
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="rounded-none border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white border border-slate-200 p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-slate-400" />
              )}
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-1">
                Welcome back
              </p>
              <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
                {user?.name}
              </h1>
              <p className="text-slate-500 text-sm">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-slate-200 p-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Total Bookings</p>
            <p className="font-display text-2xl font-bold text-slate-900 mt-1">{bookings.length}</p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Active</p>
            <p className="font-display text-2xl font-bold text-emerald-600 mt-1">
              {bookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Completed</p>
            <p className="font-display text-2xl font-bold text-slate-600 mt-1">
              {bookings.filter(b => b.status === 'completed').length}
            </p>
          </div>
        </div>

        {/* Bookings Section */}
        <div className="bg-white border border-slate-200">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900">Your Bookings</h2>
            <Button 
              onClick={() => navigate('/')}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              data-testid="browse-agencies-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Find More Agencies
            </Button>
          </div>

          {bookings.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No bookings yet</p>
              <p className="text-sm text-slate-500 mb-6">Start by finding a detective agency for your needs</p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              >
                Browse Agencies
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {bookings.map((booking) => (
                <div 
                  key={booking.booking_id} 
                  className="p-6 hover:bg-slate-50 transition-colors"
                  data-testid={`booking-${booking.booking_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-slate-900">
                          {booking.agency_name}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 border ${getStatusBadge(booking.status)}`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {booking.agency_city}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {booking.date}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {booking.service_type}
                        </div>
                      </div>

                      <p className="text-sm text-slate-600">{booking.description}</p>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-display text-lg font-bold text-slate-900">
                          {formatPrice(booking.amount)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 border ${getPaymentBadge(booking.payment_status)}`}>
                          {booking.payment_status === 'paid' ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Paid
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {booking.payment_status}
                            </span>
                          )}
                        </span>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/agency/${booking.agency_id}`)}
                        className="rounded-none border-slate-200"
                      >
                        View Agency
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
